import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/utils2";

const CATEGORIES = ["reglement", "contrat", "legal", "pv", "attestation", "facture", "autre"];
const CAT_ICON = {
  reglement: "ScrollText", contrat: "FileSignature", legal: "Scale",
  pv: "FileText", attestation: "BadgeCheck", facture: "Receipt", autre: "File",
};

const emptyForm = {
  category: "reglement", title: "", description: "", body: "", visibility: "public",
  supplier: "", service_type: "", start_date: "", end_date: "", amount: "",
  file_url: null, file_path: null, file_name: null, content_type: null, pages: null,
};

export default function Documents() {
  const { t, lang } = useApp();
  const { isRole } = useAuth();
  const navigate = useNavigate();
  const canManage = isRole("president", "tresorier", "conseil");
  const canDownload = isRole("president", "tresorier");

  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState([]);   // pages/photos à téléverser (fusionnées en 1 PDF si plusieurs)
  const fileRef = useRef(null);
  const camRef = useRef(null);

  const load = () => api.get("/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); }, []);

  const shown = filter === "all" ? docs : docs.filter((d) => d.category === filter);

  const isImage = (f) => (f.type || "").startsWith("image/");
  const clearPending = () => { pending.forEach((p) => p.url && URL.revokeObjectURL(p.url)); setPending([]); };

  const startAdd = () => { clearPending(); setForm(emptyForm); setEditingId(null); setOpen(true); };
  const startEdit = (d) => {
    clearPending();
    setForm({
      category: d.category, title: d.title, description: d.description || "", body: d.body || "",
      visibility: d.visibility || "public",
      supplier: d.supplier || "", service_type: d.service_type || "",
      start_date: d.start_date || "", end_date: d.end_date || "", amount: d.amount ?? "",
      file_url: d.file_url, file_path: d.file_path, file_name: d.file_name, content_type: d.content_type, pages: d.pages ?? null,
    });
    setEditingId(d.id); setOpen(true);
  };

  // Les photos s'accumulent (pages d'un même document) ; un PDF doit rester seul.
  const addFiles = (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length) return;
    const added = list.map((f) => ({ file: f, url: isImage(f) ? URL.createObjectURL(f) : null }));
    const next = [...pending, ...added];
    if (next.length > 1 && next.some((p) => !isImage(p.file))) {
      added.forEach((p) => p.url && URL.revokeObjectURL(p.url));
      toast.error(t("ss_single_pdf"));
      return;
    }
    setPending(next);
  };

  const removePending = (i) => setPending((prev) => {
    if (prev[i]?.url) URL.revokeObjectURL(prev[i].url);
    return prev.filter((_, idx) => idx !== i);
  });

  const submit = async () => {
    if (!form.title.trim()) return toast.error(t("doc_title"));
    setBusy(true);
    const payload = { ...form, amount: form.amount === "" ? null : Number(form.amount) };
    try {
      // Téléversement au moment d'enregistrer : toutes les pages sont fusionnées en un seul PDF.
      if (pending.length > 0) {
        setUploading(true);
        const fd = new FormData();
        pending.forEach((p) => fd.append("files", p.file));
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        payload.file_url = r.data.url; payload.file_path = r.data.path;
        payload.file_name = pending.length === 1 ? pending[0].file.name : r.data.file_name;
        payload.content_type = r.data.content_type || pending[0].file.type;
        payload.pages = r.data.pages || pending.length;
        setUploading(false);
      }
      if (editingId) await api.put(`/documents/${editingId}`, payload);
      else await api.post("/documents", payload);
      toast.success(t("saved"));
      clearPending();
      setOpen(false); setForm(emptyForm); setEditingId(null); load();
    } catch { toast.error("Erreur"); } finally { setBusy(false); setUploading(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/documents/${id}`); toast.success(t("delete")); load(); }
    catch { toast.error("Erreur"); }
  };

  const download = async (d) => {
    try {
      const r = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.file_name || "document"; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-4" data-testid="documents-page">
      <input ref={fileRef} type="file" multiple accept="application/pdf,image/*" onChange={addFiles} className="hidden" data-testid="doc-file-input" />
      <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={addFiles} className="hidden" data-testid="doc-cam-input" />

      <div className="flex items-center gap-2">
        <button onClick={() => navigate("/more")} className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center shrink-0" data-testid="documents-back">
          <Icons.ArrowLeft className="h-5 w-5 rtl:rotate-180" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-tight">{t("documents")}</h1>
          <p className="text-sm text-muted-foreground">{t("documents_sub")}</p>
        </div>
        {canManage && (
          <button onClick={startAdd} data-testid="add-document-btn"
            className="h-10 px-3 rounded-full bg-majorelle text-white font-semibold text-sm flex items-center gap-1 shrink-0">
            <Icons.Plus className="h-4 w-4" /> {t("add_document")}
          </button>
        )}
      </div>

      {!canDownload && (
        <div className="rounded-xl bg-muted px-3 py-2 text-xs text-muted-foreground flex items-center gap-2" data-testid="download-restricted-note">
          <Icons.Info className="h-4 w-4 shrink-0" /> {t("download_restricted")}
        </div>
      )}

      {/* Filtres par catégorie */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
        <FilterPill active={filter === "all"} onClick={() => setFilter("all")} testid="doc-filter-all">{t("all_cat")}</FilterPill>
        {CATEGORIES.map((c) => (
          <FilterPill key={c} active={filter === c} onClick={() => setFilter(c)} testid={`doc-filter-${c}`}>{t("doc_" + c)}</FilterPill>
        ))}
      </div>

      {open && (
        <DocForm form={form} setForm={setForm} t={t} busy={busy} uploading={uploading}
          pending={pending} onRemovePending={removePending}
          onSubmit={submit} onCancel={() => { clearPending(); setOpen(false); }}
          onChoose={() => fileRef.current?.click()} onCamera={() => camRef.current?.click()} editing={!!editingId} />
      )}

      {shown.length === 0 && (
        <div className="py-16 flex flex-col items-center text-center gap-3">
          <div className="h-16 w-16 rounded-full bg-accent flex items-center justify-center">
            <Icons.FolderOpen className="h-8 w-8 text-muted-foreground" />
          </div>
          <p className="text-muted-foreground font-medium max-w-xs">{t("no_documents")}</p>
        </div>
      )}

      <div className="space-y-3">
        {shown.map((d) => (
          <DocCard key={d.id} doc={d} t={t} lang={lang} canManage={canManage} canDownload={canDownload}
            onEdit={startEdit} onDelete={remove} onDownload={download} />
        ))}
      </div>
    </div>
  );
}

function FilterPill({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`h-9 px-3 rounded-full text-sm font-semibold whitespace-nowrap shrink-0 transition-colors ${active ? "bg-brand text-white" : "bg-secondary text-secondary-foreground"}`}>
      {children}
    </button>
  );
}

function DocCard({ doc, t, lang, canManage, canDownload, onEdit, onDelete, onDownload }) {
  const Ico = Icons[CAT_ICON[doc.category]] || Icons.File;
  const isContract = doc.category === "contrat";
  return (
    <div className="rounded-2xl bg-card border border-border p-4" data-testid={`document-${doc.id}`}>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
          <Ico className="h-5 w-5 text-brand dark:text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold">{doc.title}</p>
            <span className="text-[10px] font-bold bg-muted text-muted-foreground px-2 py-0.5 rounded-full">{t("doc_" + doc.category)}</span>
            {doc.visibility === "prive" && (
              <span className="text-[10px] font-bold bg-terracotta/15 text-terracotta px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`doc-private-badge-${doc.id}`}>
                <Icons.Lock className="h-3 w-3" /> {t("doc_private_badge")}
              </span>
            )}
          </div>
          {doc.description && <p className="text-sm text-muted-foreground mt-0.5">{doc.description}</p>}
        </div>
        {canManage && (
          <div className="flex gap-1 shrink-0">
            <button onClick={() => onEdit(doc)} data-testid={`doc-edit-${doc.id}`} className="text-muted-foreground p-1"><Icons.Pencil className="h-4 w-4" /></button>
            <button onClick={() => onDelete(doc.id)} data-testid={`doc-delete-${doc.id}`} className="text-muted-foreground p-1"><Icons.Trash2 className="h-4 w-4" /></button>
          </div>
        )}
      </div>

      {/* Détails contrat */}
      {isContract && (doc.supplier || doc.service_type || doc.amount != null) && (
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
          {doc.supplier && <Info label={t("doc_supplier")} value={doc.supplier} />}
          {doc.service_type && <Info label={t("doc_service_type")} value={doc.service_type} />}
          {doc.amount != null && <Info label={t("doc_amount")} value={`${Number(doc.amount).toLocaleString()} ${t("currency")}`} />}
          {(doc.start_date || doc.end_date) && (
            <Info label={`${t("doc_start")} → ${t("doc_end")}`}
              value={`${doc.start_date ? fmtDate(doc.start_date, lang) : "—"} → ${doc.end_date ? fmtDate(doc.end_date, lang) : t("ongoing")}`} />
          )}
        </div>
      )}

      {/* Texte du règlement */}
      {doc.body && (
        <p className="mt-3 text-sm text-foreground/90 whitespace-pre-line leading-relaxed max-h-64 overflow-y-auto rounded-xl bg-secondary/50 p-3" data-testid={`doc-body-${doc.id}`}>
          {doc.body}
        </p>
      )}

      {/* Fichier joint */}
      {doc.file_url && (
        <div className="mt-3 flex flex-wrap gap-2">
          <a href={fileUrl(doc.file_url)} target="_blank" rel="noopener noreferrer" data-testid={`doc-consult-${doc.id}`}
            className="h-10 px-4 rounded-xl bg-secondary text-secondary-foreground text-sm font-semibold flex items-center gap-1.5">
            <Icons.Eye className="h-4 w-4" /> {t("consult")}
          </a>
          {canDownload && (
            <button onClick={() => onDownload(doc)} data-testid={`doc-download-${doc.id}`}
              className="h-10 px-4 rounded-xl bg-brand text-white text-sm font-semibold flex items-center gap-1.5">
              <Icons.Download className="h-4 w-4" /> {t("download")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[11px] text-muted-foreground font-semibold">{label}</p>
      <p className="font-semibold">{value}</p>
    </div>
  );
}

function DocForm({ form, setForm, t, busy, uploading, pending, onRemovePending, onSubmit, onCancel, onChoose, onCamera, editing }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const input = "w-full h-12 rounded-xl border border-input bg-background px-3 outline-none";
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="document-form-overlay">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative w-full sm:max-w-lg max-h-[90vh] overflow-y-auto bg-card border border-border rounded-t-2xl sm:rounded-2xl p-4 pb-0 space-y-3" data-testid="document-form">
      <div>
        <label className="text-sm font-semibold text-muted-foreground">{t("doc_category")}</label>
        <div className="flex gap-2 overflow-x-auto no-scrollbar mt-1 -mx-1 px-1">
          {CATEGORIES.map((c) => {
            const Ico = Icons[CAT_ICON[c]] || Icons.File;
            const active = form.category === c;
            return (
              <button key={c} onClick={() => set("category", c)} data-testid={`doc-cat-${c}`}
                className={`h-10 px-3 rounded-xl text-sm font-semibold whitespace-nowrap shrink-0 flex items-center gap-1.5 border ${active ? "bg-brand text-white border-brand" : "bg-background text-foreground border-input"}`}>
                <Ico className="h-4 w-4" /> {t("doc_" + c)}
              </button>
            );
          })}
        </div>
      </div>

      <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder={t("doc_title")} className={input} data-testid="doc-title-input" />
      <input value={form.description} onChange={(e) => set("description", e.target.value)} placeholder={t("doc_description")} className={input} data-testid="doc-desc-input" />

      <div>
        <label className="text-sm font-semibold text-muted-foreground">{t("visibility")}</label>
        <div className="grid grid-cols-1 gap-2 mt-1">
          {[["public", "Globe", t("doc_vis_public")], ["prive", "Lock", t("doc_vis_bureau")]].map(([val, icon, label]) => {
            const Ico = Icons[icon];
            const active = form.visibility === val;
            return (
              <button key={val} onClick={() => set("visibility", val)} data-testid={`doc-vis-${val}`}
                className={`h-11 rounded-xl px-3 flex items-center gap-2 text-sm font-semibold border ${active ? "bg-brand text-white border-brand" : "bg-background text-foreground border-input"}`}>
                <Ico className="h-4 w-4" /> {label}
              </button>
            );
          })}
        </div>
      </div>

      {form.category === "reglement" && (
        <textarea value={form.body} onChange={(e) => set("body", e.target.value)} placeholder={t("doc_body_ph")} rows={8}
          className="w-full rounded-xl border border-input bg-background px-3 py-2 outline-none" data-testid="doc-body-input" />
      )}

      {form.category === "contrat" && (
        <>
          <input value={form.supplier} onChange={(e) => set("supplier", e.target.value)} placeholder={t("doc_supplier")} className={input} data-testid="doc-supplier-input" />
          <input value={form.service_type} onChange={(e) => set("service_type", e.target.value)} placeholder={t("doc_service_type")} className={input} data-testid="doc-service-input" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-muted-foreground font-semibold">{t("doc_start")}</label>
              <input type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} className={input} data-testid="doc-start-input" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground font-semibold">{t("doc_end")}</label>
              <input type="date" value={form.end_date || ""} onChange={(e) => set("end_date", e.target.value)} className={input} data-testid="doc-end-input" />
            </div>
          </div>
          <input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder={t("doc_amount")} className={input} data-testid="doc-amount-input" />
        </>
      )}

      {/* Pièce jointe : scan de fichier OU photo depuis le téléphone */}
      <div>
        <label className="text-sm font-semibold text-muted-foreground">{t("doc_file")}</label>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <button onClick={onChoose} disabled={uploading} data-testid="doc-choose-file"
            className="h-12 rounded-xl border border-input bg-background font-semibold text-sm flex items-center justify-center gap-1.5 disabled:opacity-60">
            <Icons.Upload className="h-4 w-4" /> {t("choose_file")}
          </button>
          <button onClick={onCamera} disabled={uploading} data-testid="doc-take-photo"
            className="h-12 rounded-xl border border-input bg-background font-semibold text-sm flex items-center justify-center gap-1.5 disabled:opacity-60">
            <Icons.Camera className="h-4 w-4" /> {t("take_photo_cam")}
          </button>
        </div>
        {uploading && <p className="text-xs text-muted-foreground mt-1">{t("uploading")}</p>}
        {pending.length > 0 ? (
          <div className="mt-2 space-y-2" data-testid="doc-pending-list">
            <p className="text-xs font-bold" data-testid="doc-pending-count">{pending.length} {t("ss_pages_added")}</p>
            <div className="grid grid-cols-4 gap-2">
              {pending.map((p, i) => (
                <div key={p.url || `${p.file.name}-${i}`} className="relative rounded-xl overflow-hidden border border-border bg-secondary aspect-square" data-testid={`doc-pending-${i}`}>
                  {p.url ? <img src={p.url} alt={`page ${i + 1}`} className="h-full w-full object-cover" />
                    : <div className="h-full w-full flex items-center justify-center text-muted-foreground"><Icons.FileText className="h-6 w-6" /></div>}
                  <span className="absolute bottom-0.5 start-0.5 text-[9px] font-bold bg-black/60 text-white rounded-full px-1 py-0.5">{i + 1}</span>
                  <button onClick={() => onRemovePending(i)} data-testid={`doc-pending-remove-${i}`}
                    className="absolute top-0.5 end-0.5 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center">
                    <Icons.X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">{t("doc_multi_hint")}</p>
          </div>
        ) : form.file_name && !uploading ? (
          <p className="text-xs text-brand dark:text-primary font-semibold mt-1 flex items-center gap-1" data-testid="doc-file-name">
            <Icons.Paperclip className="h-3 w-3" /> {form.file_name}
          </p>
        ) : (
          <p className="text-[11px] text-muted-foreground mt-1">{t("doc_multi_hint")}</p>
        )}
      </div>

      <div className="sticky bottom-0 -mx-4 px-4 py-3 bg-card border-t border-border flex gap-2">
        <button onClick={onCancel} data-testid="doc-cancel" className="flex-1 h-12 rounded-xl bg-secondary text-secondary-foreground font-semibold">{t("cancel")}</button>
        <button onClick={onSubmit} disabled={busy} data-testid="doc-submit" className="flex-1 h-12 rounded-xl bg-brand text-white font-bold disabled:opacity-60">
          {editing ? t("edit_document") : t("save")}
        </button>
      </div>
      </div>
    </div>
  );
}
